import ScreenOrderTiming from "@/Pages/Auth/Screens/ScreenOrderTiming";
import { cn } from "@/shared/utils/cn";
import { countryLanguages } from "@/shared/utils/constant";
import { CartContentType } from "@/shared/utils/enum";
import {
  formatPrice,
  isGreaterThanZero,
  objectIsEmpty,
} from "@/shared/utils/helpers";
import { CartItemStatus } from "@/shared/utils/interfaces";
import { OrderV2 } from "@/shared/v2/queries/interfacesV2";
import { useTranslation } from "react-i18next";
import OrderCartStatusUpdate from "./OrderCartStatusUpdate";
import OrderSelect from "./OrderSelect";
import useScreensQuery from "@/shared/queries/screens/useScreensQuery";
const Amount = ({ className = "", ...props }) => (
  <span className={cn("font-bold text-primary", className)} {...props} />
);

const Item = ({
  className = "",
  border = undefined,
  bg = undefined,
  ...props
}) => (
  <div
    className={cn(
      "mb-2 rounded-md p-4 text-2xl",
      border ? "border border-border" : "",
      bg ? "bg-bg" : "",
      className
    )}
    {...props}
  />
);

interface Props {
  orders: OrderV2;
  hidePrice?: boolean;
  isMapOrder?: boolean;
  fontSize?: any;
  bg?: any;
  language?: number;
  showStatus?: boolean;
  updateKitchen?: boolean;
  updateKitchenStatus?: boolean;
  showCategory?: boolean;
  lineIfPaid?: boolean;
  border?: any;
  customStyling?: any;
}

const feeTypes: string[] = [
  CartContentType.SERVICE_COST,
  CartContentType.DELIVERY_COST,
  CartContentType.TIP,
  CartContentType.DISCOUNT_VALUE,
];

const productTypes: string[] = [
  CartContentType.PRODUCT,
  CartContentType.DISCOUNT_PRODUCT,
  CartContentType.TOPPING,
  CartContentType.CUSTOM,
  CartContentType.CUSTOM_TOPPING,
];

const Order = ({
  border = "1px solid grey",
  orders,
  hidePrice = false,
  isMapOrder = true,
  fontSize,
  bg = "white",
  language = 1,
  showStatus = false,
  updateKitchen = false,
  updateKitchenStatus = false,
  showCategory = true,
  lineIfPaid = false,
  customStyling,
}: Props) => {
  const { t } = useTranslation();
  const { data: screens } = useScreensQuery();

  const isPaid =
    lineIfPaid &&
    orders.order_information.total_paid === orders.order_information.total_price
      ? "line-through"
      : "";

  const discountValues: Record<string, number> = orders.cart_content
    .filter((c) => c.type === CartContentType.DISCOUNT_VALUE)
    .reduce((acc, cur) => {
      acc[cur.name] =
        (acc[cur.name] || 0) + parseFloat(cur.single_price_inc_vat);
      return acc;
    }, {});

  return (
    <div style={{ fontSize: fontSize }}>
      {orders.cart_content?.map((cart, i) => {
        const {
          menu_item_id,
          quantity,
          parent_name,
          kitchen_status,
          name,
          receipt_name,
          single_price_inc_vat,
          note,
          type,
          toppings,
          is_for_another_screen,
          kitchen_start_time,
          kitchen_end_time,
        } = cart;
        const show = isMapOrder
          ? productTypes.includes(type)
          : (single_price_inc_vat !== "0" && !feeTypes.includes(type)) ||
            type === CartContentType.CUSTOM ||
            type === CartContentType.DISCOUNT_PRODUCT;
        const eNum = countryLanguages.find((l) => l.value === language)?.key;

        return show ? (
          <Item
            border={border}
            className={cn(is_for_another_screen ? "bg-error-light" : bg)}
            {...customStyling}
            key={`${menu_item_id}-${i}`}
          >
            <div
              className="flex w-full items-center"
              style={{ fontSize: fontSize }}
            >
              <Amount className="mr-4">{`${quantity}x`}</Amount>
              <div>
                {parent_name && showCategory ? `[${parent_name}]` : null}
              </div>
              <div className="ml-[5px]">{`${receipt_name[eNum] ?? name}${hidePrice ? "" : ":"}`}</div>

              {updateKitchen && (
                <div className="mx-2">
                  <OrderSelect order={orders} item={cart} screens={screens} />
                </div>
              )}
              {(is_for_another_screen ||
                (showStatus && !updateKitchenStatus)) && (
                <div className="mx-[1rem] font-bold">
                  ({t(`cartitemstatus${kitchen_status}`)})
                  {is_for_another_screen && (
                    <ScreenOrderTiming
                      bg={
                        kitchen_status === CartItemStatus.ready
                          ? "success"
                          : "error"
                      }
                      date={
                        kitchen_status === CartItemStatus.ready
                          ? kitchen_end_time
                          : kitchen_status === CartItemStatus.in_preparation
                            ? kitchen_start_time
                            : undefined
                      }
                      textAlign="center"
                    />
                  )}
                </div>
              )}
              {updateKitchenStatus && isGreaterThanZero(screens) && (
                <div className="mx-2">
                  <OrderCartStatusUpdate order={orders} item={cart} />
                </div>
              )}
              {!hidePrice && (
                <Amount textDecorationLine={isPaid} className="ml-auto">
                  {formatPrice(single_price_inc_vat)}
                </Amount>
              )}
            </div>
            {toppings?.map((top: any, i) => {
              return (
                <div
                  key={`${top.menu_item_id}-${i}`}
                  className="flex w-full items-center"
                  style={{ fontSize: fontSize }}
                >
                  <Amount className="ml-7">+</Amount>
                  <div>
                    {parent_name && showCategory
                      ? `[${top.parent_name}]`
                      : null}
                  </div>
                  <div className="ml-2">{`${top.name}${hidePrice ? "" : ":"}`}</div>
                  {!hidePrice && (
                    <Amount textDecorationLine={isPaid} className="ml-auto">
                      {formatPrice(top.single_price_inc_vat)}
                    </Amount>
                  )}
                </div>
              );
            })}
            {note && (
              <div className="flex w-full items-center text-2xl">
                <Amount className="mr-8">+</Amount>
                <div className="font-semibold" style={{ fontSize: fontSize }}>
                  {t("remarks", { value: note })}
                </div>
              </div>
            )}
          </Item>
        ) : null;
      })}

      {!isMapOrder &&
        !objectIsEmpty(discountValues) &&
        Object.entries(discountValues).map(([key, value], index) => (
          <Item key={`${index}-${key}`}>
            <div className="flex w-full items-center">
              <Amount className="mr-4">{`1x`}</Amount>
              <div>{`${key}:`}</div>
              {!hidePrice && (
                <Amount className="ml-auto">{formatPrice(value)}</Amount>
              )}
            </div>
          </Item>
        ))}
    </div>
  );
};

export default Order;
